import React, { useEffect } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";

interface SuccessStateProps {
  redditUsername: string;
}

export default function SuccessState({ redditUsername }: SuccessStateProps) {
  useEffect(() => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 50 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    let intervalId: any;
    intervalId = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(intervalId);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-xl mx-auto bg-white border-2 border-indigo-100 rounded-3xl p-8 text-slate-800 shadow-2xl shadow-indigo-100/50 text-center relative overflow-hidden"
    >
      {/* Success Icon — animated green tick that draws itself in */}
      <div className="w-20 h-20 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-6 relative">
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-emerald-500"
          initial={{ scale: 1, opacity: 0.4 }}
          animate={{ scale: 1.4, opacity: 0 }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeOut" }}
        />
        <motion.svg viewBox="0 0 80 80" className="w-11 h-11 relative z-10">
          <motion.circle
            cx="40"
            cy="40"
            r="35"
            fill="none"
            stroke="#059669"
            strokeWidth="6"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          />
          <motion.path
            d="M23 41 L35 53 L58 28"
            fill="none"
            stroke="#059669"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.55, ease: "easeOut" }}
          />
        </motion.svg>
      </div>

      <h3 className="text-3xl font-black mb-2 text-[#3F51B5]">
        Application Submitted!
      </h3>
      <p className="text-slate-600 text-sm max-w-sm mx-auto mb-8 font-medium">
        Congratulations! Your Reddit account <strong className="text-slate-950 font-bold">u/{redditUsername}</strong> is eligible. Your application is now in review.
      </p>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => (window.location.href = "/")}
          className="flex-1 py-4 px-6 rounded-full font-extrabold border-2 border-[#3F51B5] text-[#3F51B5] hover:bg-indigo-50 transition-all duration-300 flex items-center justify-center gap-1 text-md"
        >
          Back to Home
        </button>
      </div>
    </motion.div>
  );
}
